import type { TenantCompanyProfile } from '@forq/platform-data';
import {
  emptyDatevSettings,
  emptySellerSettings,
  type DatevSettings,
  type SellerSettings,
} from '@forq/finance-data';

/**
 * Verkäufer- und DATEV-Angaben aus der Grundkonfiguration ableiten -- EINE Quelle.
 *
 * BEFUND: Beides lag zusätzlich in eigenen `localStorage`-Inseln (`forq.finance.seller.*`,
 * `forq.finance.datev.*`). Damit hing die USt-IdNr. auf der XRechnung und die Beraternummer im
 * DATEV-Export am BROWSER des jeweiligen Mitarbeiters, nicht am Mandanten: Ein Kollege am zweiten
 * Rechner exportierte mit anderen -- oder gar keinen -- Angaben, ohne dass es jemandem auffiel.
 *
 * Die Grundkonfiguration (`company_profiles.profile`) führt genau dieselben Felder bereits, wird
 * je Mandant in der Datenbank gehalten und hat in der Administration einen eigenen Editor. Diese
 * Datei bildet sie auf die beiden Finance-Formen ab; geschrieben wird nur noch dort.
 */

/** Der Verkäufer einer XRechnung (EN 16931) aus der Grundkonfiguration. */
export function sellerSettingsFromProfile(profile: TenantCompanyProfile | null): SellerSettings {
  if (!profile) return emptySellerSettings;
  const { identity, contact, bank } = profile;
  // Der Firmenname auf dem Beleg schließt die Rechtsform ein („Fördertechnik Werl GmbH"), das
  // Identitätsfeld trennt beides. Doppelte Nennung vermeiden, falls sie schon im Namen steht.
  const legalForm = identity.legalForm.trim();
  const name = identity.name.trim();
  const fullName = legalForm && !name.toLowerCase().endsWith(legalForm.toLowerCase())
    ? `${name} ${legalForm}`.trim()
    : name;
  return {
    name: fullName,
    street: identity.address.street.trim(),
    postalCode: identity.address.postalCode.trim(),
    city: identity.address.city.trim(),
    countryCode: (identity.address.countryCode || 'DE').trim().toUpperCase(),
    vatId: identity.vatId.trim(),
    taxNumber: identity.taxNumber.trim(),
    // Ansprechpartner ist in der XRechnung Pflicht (BG-6); fehlt ein eigener, gilt die Firmenanschrift.
    contactName: contact.contactName.trim(),
    contactPhone: (contact.contactPhone || contact.phone).trim(),
    contactEmail: (contact.contactEmail || contact.email).trim(),
    iban: bank.iban.replace(/\s/g, '').toUpperCase(),
    bic: bank.bic.replace(/\s/g, '').toUpperCase(),
    bankName: bank.bankName.trim(),
  };
}

/** Das DATEV-Mandat für den EXTF-Kopf aus der Grundkonfiguration. */
export function datevSettingsFromProfile(profile: TenantCompanyProfile | null): DatevSettings {
  if (!profile) return emptyDatevSettings;
  const { datev } = profile;
  return {
    consultantNumber: String(datev.consultantNumber ?? '').trim(),
    clientNumber: String(datev.clientNumber ?? '').trim(),
    fiscalYearStart: String(datev.fiscalYearStart ?? '').trim(),
    accountLength: Number(datev.accountLength ?? 4),
  };
}

/** Wo der Anwender die Angaben ändert -- der Text steht an mehreren Stellen, deshalb hier einmal. */
export const grundkonfigurationHinweis =
  'Diese Angaben stammen aus der Grundkonfiguration (Administration → Grundkonfiguration) und gelten für den ganzen Mandanten.';
